import 'pages'
import React, { useContext } from 'react'
import { ToastContainer } from 'react-toastify'
import { Router } from 'components/router/router'
import { ClientRouter } from 'components/router/client-router'
import { ThemeProvider } from '@material-ui/core'
import { ThemeProvider as NewMuiProvider } from '@mui/material'
import 'react-toastify/dist/ReactToastify.min.css'
import { useAppTheme } from '@marketing-milk/frontend'
import { createV4Theme } from './theme/muiV4Theme'
import { createV5Theme } from './theme/muiV5Theme'
import { SessionContextProvider, BusinessContextProvider } from '@marketing-milk/frontend'
import { sessionContext } from '@marketing-milk/frontend'
import './index.css'
import { QueryClientProvider, QueryClient } from 'react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { Redirect } from 'react-router-dom'
import Auth0ProviderWithHistory from 'Auth0ProviderWithHistory'
import { Permissions } from '@marketing-milk/interfaces'
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js'

const reactQueryClient = new QueryClient()

const postHogOptions = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  defaults: '2025-05-24',
} as const

function AppComponent() {
  const { user } = useContext(sessionContext)
  posthog.identify(user?.email)
  // Theme needs to stay static 'light' theme because if user has dark mode
  // enabled from Client, only material components will have dark mode styles.
  const theme = createV4Theme(useAppTheme('light'))
  // Same palette, expressed the way v5 reads it — otherwise every v5 component
  // falls back to stock Material blue.
  const v5Theme = createV5Theme(theme)

  // Undetermined user (still loading) falls into the full Router, same as
  // before this branch existed — it's the Router's own login/redirect flow
  // that handles that state, not this gate.
  const hasBuilderAccess = !user || user.permissions.includes(Permissions.AccessAdBuilderApp)
  const hasPacketReviewAccess = user?.permissions.includes(Permissions.AccessPacketReview)

  return (
    <ThemeProvider theme={theme}>
      <NewMuiProvider theme={v5Theme}>
        {hasBuilderAccess || hasPacketReviewAccess ? (
          <>
            <ToastContainer style={{ zIndex: 5000 }} />
            {hasBuilderAccess ? <Router /> : <ClientRouter />}
          </>
        ) : (
          <Unauthorized />
        )}
      </NewMuiProvider>
    </ThemeProvider>
  )
}

function App() {
  return (
    <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY || ''} options={postHogOptions}>
      <QueryClientProvider client={reactQueryClient}>
        <SessionContextProvider>
          <BusinessContextProvider>
            <AppComponent />
          </BusinessContextProvider>
        </SessionContextProvider>
      </QueryClientProvider>
    </PostHogProvider>
  )
}

const Unauthorized = () => {
  const { isAuthenticated, logout: auth0Logout } = useAuth0()
  const { logout } = useContext(sessionContext)

  function logoutUser() {
    const redirectURL = 'https://www.marketingmilk.com/'
    logout()

    // If the user logged in via Auth0, we need to log them out of Auth0
    // in addition to clearing their Marketing Milk session
    if (isAuthenticated) {
      auth0Logout({ returnTo: redirectURL })
    }

    return <Redirect to={redirectURL} />
  }

  return (
    <div className="loading m-grid__item m-grid__item--fluid m-grid  m-error-1">
      <div className="m-error_container">
        <span className="m-error_number">
          <h1 className="text-admin">Hi,</h1>
        </span>
        <div className="m-error_desc">
          You are not authorized to view this page. To login with another account, click{' '}
          <a onClick={logoutUser}>here</a>
        </div>
      </div>
    </div>
  )
}

export default App
